import { useUserStore } from '../../stores/prioritization/userStore';
import { buildCapabilityChecker } from '../../lib/authorization';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useIsAdmin } from './useScopedPermissions';
import { getTrackOwnerUserIdFromMetadata, type Track } from '../../types/prioritization/track';
import { canChangeTrackCriteria, canChangeTrackLock, TRACK_LOCK_RIGHTS } from '../../lib/trackCriteriaVersions';

type Gate = { allowed: boolean; reason?: string };
export interface TrackAuthority {
  lockGate: Gate;
  criteriaGate: Gate;
}

/** The person's standing, read once; each track's gates are worked out from it. */
function useActor() {
  const userId = useUserStore((state) => state.userId);
  // Redraw when capabilities finish loading: can() is one stable function, and it
  // asks at the store's current scope, which misses a role granted on the workspace
  // until that scope is set. The checker also reads the capabilities themselves.
  useUserStore((state) => state.resolvedCapabilityIds);
  const can = buildCapabilityChecker();
  const isWorkspaceAdmin = useIsAdmin();
  return {
    userId,
    isWorkspaceAdmin,
    holdsLockRight: TRACK_LOCK_RIGHTS.some((right) => can(right)),
    holdsCriteriaRight: can('criteria.select'),
  };
}

function authorityFor(
  actor: ReturnType<typeof useActor>,
  cycle: { ownerId?: string | null; createdBy?: string | null } | undefined,
  track: Pick<Track, 'metadata'>,
): TrackAuthority {
  return {
    lockGate: canChangeTrackLock(
      { userId: actor.userId, isWorkspaceAdmin: actor.isWorkspaceAdmin, holdsLockRight: actor.holdsLockRight },
      cycle?.ownerId,
    ),
    criteriaGate: canChangeTrackCriteria(
      { userId: actor.userId, isWorkspaceAdmin: actor.isWorkspaceAdmin, holdsCriteriaRight: actor.holdsCriteriaRight },
      {
        cycleOwnerId: cycle?.ownerId,
        cycleCreatedBy: cycle?.createdBy,
        trackOwnerId: getTrackOwnerUserIdFromMetadata(track.metadata),
      },
    ),
  };
}

/**
 * Who may do what on a track, read before the click, so the track page draws no
 * control the server would refuse (build contract §2.8): `lockGate` for the track's
 * locks (711), `criteriaGate` for adding and removing a criterion (1006).
 */
export function useTrackAuthority(track: Pick<Track, 'cycleId' | 'metadata'>): TrackAuthority {
  const actor = useActor();
  const cycle = useDegapProcessStore((s) => (track.cycleId ? s.getCycle(track.cycleId) : undefined));
  return authorityFor(actor, cycle, track);
}

/**
 * The same gates for every track of one cycle, for a cycle-level screen that writes
 * to several tracks at once (the cycle configure wizard): `authorityOf(track)`.
 */
export function useCycleTracksAuthority(cycleId: string | null | undefined): (track: Pick<Track, 'metadata'>) => TrackAuthority {
  const actor = useActor();
  const cycle = useDegapProcessStore((s) => (cycleId ? s.getCycle(cycleId) : undefined));
  return (track) => authorityFor(actor, cycle, track);
}
